import React from 'react';
import {
  MapCardContainer,
  ImageSection,
  TextSection,
  LocationType,
  Address,
  CTASection
} from './styles';
import MobileCancel from './MobileCancel';
import DesktopPhone from './DesktopPhone';
import MobilePhone from './MobilePhone';
import LocationName from './LocationName';
import NonClickableLocationName from './NonClickableLocationName';
import LocationImage from './LocationImage';
import NonClickableLocationImage from './NonClickableLocationImage';
import GoogleMapsLink from './GoogleMapsLink';
import Accessibility from './Accessibility';
import CTAButton from './CTAButton';
import {
  getHeroImage,
  getLocationDisplayAddress,
  getLocationUrl,
  getPhone
} from './utils';
import {
  object,
  string,
  func,
  shape,
  arrayOf,
  oneOfType,
  node
} from 'prop-types';
import { useIntl } from '@kyruus/intl';
import { messages } from '../../lib/messages';

function MapCard({
  locationData,
  currentQuery,
  ctaText,
  onCtaClickHandler,
  bookOnlineUrl,
  onMobileCancelHandler,
  onLocationClick,
  closeButtonRef
}) {
  const intl = useIntl();

  const {
    name = '',
    exterior_signage_name = '',
    location_type = {},
    address = {},
    contacts = [],
    media = [],
    id,
    accessibility = [],
    primary_marketable_location_id
  } = locationData;

  const renderCta = !!(ctaText && (onCtaClickHandler || bookOnlineUrl));

  const closeLabel = intl.formatMessage(messages.mapCloseAriaLabel);
  return (
    <MapCardContainer data-testid="MapCard">
      <ImageSection>
        {primary_marketable_location_id ? (
          <LocationImage
            imgUrl={getHeroImage(media)}
            locationUrl={getLocationUrl(name, id, currentQuery)}
            onLocationClick={onLocationClick}
          />
        ) : (
          <NonClickableLocationImage imgUrl={getHeroImage(media)} />
        )}
        <MobileCancel
          data-testid="MapCardCancel"
          aria-label={closeLabel}
          onMobileCancelHandler={onMobileCancelHandler}
          ref={closeButtonRef}
        />
      </ImageSection>
      <TextSection>
        {primary_marketable_location_id ? (
          <LocationName
            locationUrl={getLocationUrl(name, id, currentQuery)}
            onLocationClick={onLocationClick}
          >
            {name}
          </LocationName>
        ) : (
          <NonClickableLocationName>{name}</NonClickableLocationName>
        )}
        {getPhone(contacts) && <MobilePhone contacts={contacts} />}
        <LocationType>{location_type.name}</LocationType>
        <Address>{getLocationDisplayAddress(address)}</Address>{' '}
        <GoogleMapsLink name={exterior_signage_name || name} address={address}>
          (Map)
        </GoogleMapsLink>
        <Accessibility accessibility={accessibility} />
      </TextSection>
      <CTASection>
        {renderCta && (
          <CTAButton
            ctaText={ctaText}
            onCtaClickHandler={onCtaClickHandler}
            bookOnlineUrl={bookOnlineUrl}
          />
        )}
        {getPhone(contacts) && (
          <DesktopPhone contacts={contacts} isLeftAligned={renderCta} />
        )}
      </CTASection>
    </MapCardContainer>
  );
}

MapCard.propTypes = {
  locationData: shape({
    name: string.isRequired,
    location_type: shape({
      name: string
    }),
    address: shape({
      street1: string,
      street2: string,
      city: string,
      state: string,
      zip: string
    }),
    contacts: arrayOf(object),
    media: arrayOf(object),
    id: string.isRequired,
    accessibility: arrayOf(object),
    primary_marketable_location_id: string
  }).isRequired,
  currentQuery: string.isRequired,
  ctaText: string,
  onCtaClickHandler: func,
  onMobileCancelHandler: func.isRequired,
  onLocationClick: func.isRequired,
  closeButtonRef: oneOfType([
    func,
    shape({
      current: node
    })
  ])
};

export default MapCard;
